/**
 * Calendar - Clean, minimal calendar with day/week/month views
 */

import { useState } from 'react'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { cn } from '../lib/utils'

type View = 'day' | 'week' | 'month'

interface Event {
  id: string
  title: string
  time: string
  duration: number // minutes
  type: 'meeting' | 'task' | 'reminder'
  attendees?: string[]
}

const MOCK_EVENTS: Record<string, Event[]> = {
  '2024-01-15': [
    { id: '1', title: 'Team standup', time: '09:00', duration: 30, type: 'meeting', attendees: ['Maya', 'Carlos'] },
    { id: '2', title: 'Review PR #2072', time: '11:00', duration: 60, type: 'task' },
    { id: '3', title: 'Lunch with investors', time: '12:30', duration: 90, type: 'meeting' },
  ],
  '2024-01-16': [
    { id: '4', title: 'Sprint planning', time: '10:00', duration: 120, type: 'meeting', attendees: ['Team'] },
  ],
  '2024-01-17': [
    { id: '5', title: 'Code review', time: '14:00', duration: 60, type: 'task' },
    { id: '6', title: 'Ship v2.0', time: '17:00', duration: 30, type: 'reminder' },
  ],
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export default function Calendar() {
  const [view, setView] = useState<View>('week')
  const [currentDate, setCurrentDate] = useState(new Date())

  const today = new Date()
  const isToday = (date: Date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()

  const formatDateKey = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

  const navigateDate = (direction: number) => {
    const newDate = new Date(currentDate)
    if (view === 'day') newDate.setDate(newDate.getDate() + direction)
    else if (view === 'week') newDate.setDate(newDate.getDate() + direction * 7)
    else newDate.setMonth(newDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  const getWeekDays = () => {
    const start = new Date(currentDate)
    start.setDate(start.getDate() - start.getDay())
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(start)
      day.setDate(start.getDate() + i)
      return day
    })
  }

  const getMonthDays = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const days: (Date | null)[] = Array(startPadding).fill(null)

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">
            {view === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            {view === 'week' && `Week of ${getWeekDays()[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            {view === 'month' && `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
            {(['day', 'week', 'month'] as View[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md capitalize transition-colors',
                  view === v ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
                )}
              >
                {v}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate(-1)}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => navigateDate(1)}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Add Event */}
          <button className="flex items-center gap-2 px-4 py-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors">
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">New</span>
          </button>
        </div>
      </div>

      {/* Calendar Views */}
      {view === 'day' && <DayView date={currentDate} events={MOCK_EVENTS[formatDateKey(currentDate)] || []} />}
      {view === 'week' && <WeekView days={getWeekDays()} events={MOCK_EVENTS} isToday={isToday} formatDateKey={formatDateKey} />}
      {view === 'month' && <MonthView days={getMonthDays()} events={MOCK_EVENTS} isToday={isToday} formatDateKey={formatDateKey} currentMonth={currentDate.getMonth()} />}
    </div>
  )
}

function DayView({ events }: { date: Date; events: Event[] }) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 8) // 8am to 7pm

  return (
    <div className="space-y-1">
      {hours.map(hour => {
        const hourEvents = events.filter(e => parseInt(e.time.split(':')[0]) === hour)
        return (
          <div key={hour} className="flex gap-4">
            <span className="w-16 text-sm text-gray-500 text-right py-3">
              {hour > 12 ? hour - 12 : hour}{hour >= 12 ? 'pm' : 'am'}
            </span>
            <div className="flex-1 border-t border-white/5 py-2 min-h-[60px]">
              {hourEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function WeekView({ days, events, isToday, formatDateKey }: {
  days: Date[]
  events: Record<string, Event[]>
  isToday: (d: Date) => boolean
  formatDateKey: (d: Date) => string
}) {
  return (
    <div className="grid grid-cols-7 gap-4">
      {days.map(day => (
        <div key={day.toISOString()}>
          <div className={cn(
            'text-center pb-4 mb-4 border-b border-white/5',
            isToday(day) && 'text-lambda-gold'
          )}>
            <p className="text-xs text-gray-500 uppercase">{DAYS[day.getDay()]}</p>
            <p className={cn(
              'text-2xl font-light mt-1',
              isToday(day) ? 'text-lambda-gold' : 'text-white'
            )}>
              {day.getDate()}
            </p>
          </div>
          <div className="space-y-2">
            {(events[formatDateKey(day)] || []).map(event => (
              <EventCard key={event.id} event={event} compact />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function MonthView({ days, events, isToday, formatDateKey, currentMonth }: {
  days: (Date | null)[]
  events: Record<string, Event[]>
  isToday: (d: Date) => boolean
  formatDateKey: (d: Date) => string
  currentMonth: number
}) {
  return (
    <div>
      <div className="grid grid-cols-7 gap-px mb-2">
        {DAYS.map(day => (
          <div key={day} className="text-center text-xs text-gray-500 uppercase py-2">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-white/5 rounded-xl overflow-hidden">
        {days.map((day, i) => (
          <div
            key={i}
            className={cn(
              'min-h-[100px] p-2 bg-[#0a0a0f]',
              day && day.getMonth() !== currentMonth && 'opacity-30'
            )}
          >
            {day && (
              <>
                <span className={cn(
                  'inline-flex w-7 h-7 items-center justify-center rounded-full text-sm',
                  isToday(day) ? 'bg-lambda-gold text-black' : 'text-gray-400'
                )}>
                  {day.getDate()}
                </span>
                <div className="mt-1 space-y-1">
                  {(events[formatDateKey(day)] || []).slice(0, 2).map(event => (
                    <div
                      key={event.id}
                      className={cn(
                        'text-xs px-1.5 py-0.5 rounded truncate',
                        event.type === 'meeting' && 'bg-blue-500/20 text-blue-400',
                        event.type === 'task' && 'bg-lambda-gold/20 text-lambda-gold',
                        event.type === 'reminder' && 'bg-purple-500/20 text-purple-400'
                      )}
                    >
                      {event.title}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function EventCard({ event, compact = false }: { event: Event; compact?: boolean }) {
  return (
    <div className={cn(
      'rounded-lg transition-colors cursor-pointer',
      event.type === 'meeting' && 'bg-blue-500/10 hover:bg-blue-500/20 border-l-2 border-blue-500',
      event.type === 'task' && 'bg-lambda-gold/10 hover:bg-lambda-gold/20 border-l-2 border-lambda-gold',
      event.type === 'reminder' && 'bg-purple-500/10 hover:bg-purple-500/20 border-l-2 border-purple-500',
      compact ? 'p-2' : 'p-3'
    )}>
      <p className={cn('text-white', compact ? 'text-xs' : 'text-sm')}>{event.title}</p>
      <p className={cn('text-gray-500', compact ? 'text-[10px]' : 'text-xs')}>
        {event.time} · {event.duration}min
      </p>
    </div>
  )
}
